import { fontSizes } from "@/constants/constants";
import { ButtonCta, LiveChatButton } from "@/components";
import React from "react";

const defaultTitle = (
  <>
    A Book Publishing Company Built for{" "}
    <span className="text-gradient italic">Authors</span>
  </>
);

const defaultDescription = (
  <>
    Prime Publishing Authors is a full-service book publishing company. We help
    authors write, edit, design, publish, and market their books — turning your
    manuscript into a finished, professionally published title and amplifying
    its reach. From first edit to launch day, we keep the process clear, timely,
    and accountable.
  </>
);

const AboutHero = ({ title = defaultTitle, description = defaultDescription }) => {
  return (
    <section className="relative w-full overflow-hidden bg-paper bg-paper-grain">
      <div aria-hidden className="pointer-events-none absolute -left-24 top-20 h-72 w-72 rounded-full bg-brand-200/25 blur-3xl" />
      <div aria-hidden className="pointer-events-none absolute -right-16 top-40 h-72 w-72 rounded-full bg-gold-soft/20 blur-3xl" />

      <div className="relative mx-auto grid w-full max-w-[1350px] grid-cols-1 items-center gap-12 px-5 py-16 md:py-20 lg:grid-cols-2 lg:gap-16">
        {/* Text */}
        <div className="text-center lg:text-left">
          <span className="eyebrow flex items-center justify-center gap-3 text-gold-deep lg:justify-start">
            <span className="h-px w-8 bg-gold/70" />
            About Prime Publishing Authors
          </span>
          <h1 className={`mt-5 font-display font-semibold leading-[1.05] text-ink ${fontSizes.text_7_xl}`}>
            {title}
          </h1>
          <p className={`mt-6 leading-relaxed text-ink-soft ${fontSizes.base}`}>
            {description}
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4 lg:justify-start">
            <ButtonCta buttonText="Start Your Project" classStyle="text-white bg-brand-600 hover:bg-brand-700 h-12" />
            <LiveChatButton buttonText="Talk to Us" classStyle="text-ink bg-transparent border border-ink/15 hover:border-gold/60 hover:bg-white h-12" />
          </div>

          <div className="mt-10 flex flex-wrap items-center justify-center gap-x-8 gap-y-4 lg:justify-start">
            {[
              { v: "500+", l: "Titles Published" },
              { v: "4.9/5", l: "Author Rating" },
              { v: "100+", l: "Retail Partners" },
            ].map((s) => (
              <div key={s.l} className="text-left">
                <p className="font-display text-2xl font-semibold text-brand-700">{s.v}</p>
                <p className="text-xs uppercase tracking-[0.16em] text-ink-soft">{s.l}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Image collage */}
        <div className="relative flex justify-center">
          <div className="relative w-full max-w-md">
            <div className="absolute -inset-4 rounded-[2.2rem] border border-gold/25" />
            <div className="absolute -inset-4 rounded-[2.2rem] bg-gradient-to-br from-brand-100/40 to-gold-soft/20 blur-lg" />
            <div className="relative z-10 overflow-hidden rounded-[1.8rem] shadow-editorial ring-1 ring-black/5">
              <img loading="eager" fetchPriority="high" decoding="async" src="/images/achievement-img.webp" alt="Prime Publishing Authors" className="h-[420px] w-full object-cover" />
            </div>
            <img loading="lazy" decoding="async" src="/images/book-covers/hero-slide-8.webp" alt="" aria-hidden className="absolute -left-8 bottom-8 z-20 hidden w-24 rotate-[-8deg] rounded-lg shadow-2xl ring-1 ring-black/10 md:block animate-float-slow" />
            <img loading="lazy" decoding="async" src="/images/book-covers/hero-slide-4.webp" alt="" aria-hidden className="absolute -right-6 -top-6 z-20 hidden w-24 rotate-[7deg] rounded-lg shadow-2xl ring-1 ring-black/10 md:block animate-float" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutHero;
